/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ModifiedAssessment, ModifiedTarget } from 'src/languageStudio/appShell/prebuiltDemos/sentimentDemo/VisualizedSentimentResult';
import { ModelType } from 'src/luis/types/ModelType';
import { Label } from 'src/modules/labeling/types/Label';
import { AnnotationData } from 'src/packages/react-text-annotator/lib/esm';

type ResultToken = { id: string; tokens: { startTokenIndex: number; endTokenIndex: number } };

export const getLabelId = (sentenceIndex: number, assessmentIndex: number, labelType: 'target' | 'assessment'): string =>
    `sentence${sentenceIndex}${labelType}${assessmentIndex}`;

export const getLabel = (
    name: string,
    targetIndex: number,
    tokens: ResultToken[],
    sentenceIndex: number,
    labelType: 'target' | 'assessment',
    startToken: number = 0
): Label => {
    const id = getLabelId(sentenceIndex, targetIndex, labelType);
    const expTokens = tokens.find(t => t.id === id).tokens;

    return {
        depth: 1,
        renderingLevel: 1,
        renderedAs: 'line',
        end: expTokens.endTokenIndex - startToken,
        start: expTokens.startTokenIndex - startToken,
        intrinsicEnd: expTokens.endTokenIndex - startToken,
        viewOptions: { isDashed: labelType === 'assessment' },
        intrinsicStart: expTokens.startTokenIndex - startToken,
        entity: {
            id,
            name,
            roles: [],
            typeId: ModelType.Simple
        }
    };
};

export const getRelationAnnotations = (
    relationName: string,
    relationColor: string,
    targets: ModifiedTarget[],
    assessments: ModifiedAssessment[],
    targetsTokens: {
        id: string;
        tokens: {
            startTokenIndex: number;
            endTokenIndex: number;
        };
    }[],
    assessmentsTokens: {
        id: string;
        tokens: {
            startTokenIndex: number;
            endTokenIndex: number;
        };
    }[],
    startToken: number = 0
): AnnotationData[] =>
    targets.reduce(
        (annotations, target) =>
            annotations.concat(
                assessments
                    .filter(assessmentToFilter =>
                        target.target.relations.some(
                            relation =>
                                relation.ref ===
                                `#/documents/0/sentences/${assessmentToFilter.sentenceIndex}/assessments/${assessmentToFilter.assessmentIndex}`
                        )
                    )
                    .map(relatedAssessment => {
                        const targetTokens = targetsTokens.find(
                            t => t.id === getLabelId(target.sentenceIndex, target.targetIndex, 'target')
                        ).tokens;
                        const assessmentTokens = assessmentsTokens.find(
                            t => t.id === getLabelId(relatedAssessment.sentenceIndex, relatedAssessment.assessmentIndex, 'assessment')
                        ).tokens;

                        return {
                            name: relationName,
                            kind: 'relation',
                            color: relationColor,
                            endToken: targetTokens.startTokenIndex - startToken,
                            startToken: assessmentTokens.startTokenIndex - startToken,
                            id: `${target.targetIndex}-${relatedAssessment.assessmentIndex}-relation`
                        };
                    })
            ),
        []
    );
